import * as turf from '@turf/turf'
import {
  InputType,
  RolodexEntryJson,
  RolodexEntryType,
  UnitMagnitude,
} from 'types'
import { parseGeoJsonGeometry, parseString } from 'utils/parsingHelpers'
import { compareAlphanumeric } from 'utils/stringHelpers'

import { GeckoEntryBase } from './geckoEntry'

export type GeckoAddress = {
  address_line_1: string
  address_line_2: string | undefined
  city: string
  region: string
  country: string
  postal_code: string
}

export type GeckoSiteGrouping = {
  id: string
  name: string
  description: string
  parent: string | undefined
  category: string | undefined
}

export type GeckoSiteCustomField = {
  key: string
  fieldName: string
  displayName: string
  inputType: InputType
  required: boolean
  unitMagnitude: UnitMagnitude | undefined
}

export function isGeckoSite(entry: GeckoEntryBase): entry is GeckoSite {
  return entry instanceof GeckoSite
}

/**
 * A location such as a power plant or missile silo that has an address,
 * associated GeoJSON geometry, and contains units.
 */
export class GeckoSite extends GeckoEntryBase<
  {
    name: string
    slack_channel: string | undefined
    address: GeckoAddress | undefined
    geography: turf.Geometry | undefined
    groupings: Readonly<{
      [groupingID: string]: GeckoSiteGrouping
    }>
    custom_fields?: Readonly<GeckoSiteCustomField[]>
  },
  {
    organization: string
    [RolodexEntryType.SALESFORCE_ACCOUNT]: string
  }
> {
  //----------------------------------------------------------------------------

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    const addressJSON =
      (json.data.address as
        | Record<string, Record<string, string>>
        | undefined) ?? {}

    const groupingJSON =
      (json.data.groupings as Record<string, GeckoSiteGrouping> | undefined) ??
      {}

    const customFieldsJSON =
      (json.data.custom_fields as GeckoSiteCustomField[] | undefined) ?? []

    super(
      json,
      {
        name: parseString(json.data.name)!, // name is a required field for sites, TODO ARCH-203: remove this "!"
        slack_channel: parseString(json.data.slack_channel),
        address: addressJSON
          ? {
              address_line_1: parseString(addressJSON['address_line_1']) ?? '',
              address_line_2: parseString(addressJSON['address_line_2'] ?? ''),
              city: parseString(addressJSON['city']) ?? '',
              region: parseString(addressJSON['region']) ?? '',
              country: parseString(addressJSON['country']) ?? '',
              postal_code: parseString(addressJSON['postal_code']) ?? '',
            }
          : undefined,
        geography: parseGeoJsonGeometry(json.data.geography),
        groupings: groupingJSON,
        custom_fields: customFieldsJSON,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        [RolodexEntryType.SALESFORCE_ACCOUNT]:
          parseString(json?.tags?.[RolodexEntryType.SALESFORCE_ACCOUNT]) ?? '',
      },
      {}
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A simple factory method which creates a site from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoSite(json)
  }

  /**
   * A simple comparison method, used to sort entries by their names, properly
   * taking into account multi-digit numbers (i.e. 2 will appear before 20)
   * @param a     The first entry
   * @param b     The second entry
   */
  public static DefaultSort(a: GeckoSite, b: GeckoSite) {
    return compareAlphanumeric(a.data.name, b.data.name)
  }
}
